/**
 * Alta y edición de cliente.
 *
 * El NIF es opcional en el alta a propósito: un cliente potencial se
 * registra antes de tener sus datos fiscales completos, y exigirlos aquí
 * empuja a la gente a inventarse un valor de relleno que después nadie
 * corrige. La API lo exige donde de verdad importa — al facturar.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { useForm } from '../../forms/use-form.js';
import { FormSection, SelectField, TextArea, TextField } from '../../forms/fields.js';
import { email as emailRule, maxLength, required, type FieldRules } from '../../forms/form-state.js';

interface CustomerDetail {
  id: string;
  code: string;
  legal_name: string;
  trade_name: string | null;
  tax_id: string | null;
  customer_type: string;
  email: string | null;
  phone: string | null;
  payment_terms: string | null;
  sales_rep_user_id: string | null;
  accounting_code: string | null;
  accounting_account: string | null;
  notes: string | null;
  version: number;
  // Comunidad de propietarios
  property_manager_id: string | null;
  president_name: string | null;
  president_phone: string | null;
  president_email: string | null;
  maintenance_contact_name: string | null;
  maintenance_contact_phone: string | null;
}

interface SalesRep {
  id: string;
  full_name: string;
  email: string;
  customer_count: string;
}

const TYPES = [
  { value: 'COMPANY', label: 'Empresa' },
  { value: 'INDIVIDUAL', label: 'Particular' },
  { value: 'PUBLIC_ADMIN', label: 'Administración pública' },
  { value: 'COMMUNITY', label: 'Comunidad de propietarios' },
];

interface Administrador {
  id: string;
  name: string;
  community_count: number;
}

const RULES: FieldRules = {
  // En el alta lo pone el programa; al editar se conserva el que tiene.
  code: [maxLength(32)],
  legalName: [required('La razón social'), maxLength(255)],
  taxId: [maxLength(20)],
  email: [emailRule()],
  phone: [maxLength(32)],
};

const NULLABLE = ['taxId', 'branchId', 'salesRepUserId', 'accountingCode', 'accountingAccount'];

export function CustomerFormScreen(): React.ReactNode {
  const { api } = useSession();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editing = id !== undefined && id !== 'nuevo';

  const [detail, setDetail] = useState<CustomerDetail | null>(null);

  /**
   * El cliente que ya tiene ese NIF, si lo hay.
   *
   * Se consulta al salir del campo, antes de rellenar el resto: enterarse al
   * guardar, con la ficha entera escrita, es tarde y molesta.
   */
  const [yaExiste, setYaExiste] = useState<{
    id: string;
    code: string;
    legalName: string;
    sites: { id: string; code: string; name: string }[];
  } | null>(null);

  const comprobarNif = useCallback(
    async (nif: string) => {
      // Sólo en el alta: al editar, el NIF que hay es el suyo.
      if (id !== undefined || nif.trim() === '') {
        setYaExiste(null);
        return;
      }
      try {
        const r = await api.get<{ customer: typeof yaExiste }>(
          `/customers/por-nif/${encodeURIComponent(nif.trim())}`,
        );
        setYaExiste(r.customer);
      } catch {
        // Que falle la comprobación no puede impedir dar de alta: si el NIF
        // estuviera repetido, el servidor lo rechazará igualmente al guardar.
        setYaExiste(null);
      }
    },
    [api, id],
  );
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [administradores, setAdministradores] = useState<Administrador[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ items: SalesRep[] }>('/customers/sales-reps')
      .then((data) => {
        if (!cancelled) setSalesReps(data.items);
      })
      // Sin comerciales el formulario sigue siendo usable: se queda el
      // desplegable con «Sin asignar» y el alta no se bloquea por esto.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ items: Administrador[] }>('/property-managers')
      .then((data) => {
        if (!cancelled) setAdministradores(data.items);
      })
      // Igual que con los comerciales: sin administradores el alta sigue
      // siendo posible, sólo que sin poder asignarlo todavía.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;

    api
      .get<CustomerDetail>(`/customers/${id}`)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setLoadError(
            caught instanceof ApiError ? caught.message : 'No se ha podido cargar el cliente.',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, editing, id]);

  const form = useForm<{ id: string } | void>({
    mode: editing ? 'edit' : 'create',
    rules: RULES,
    nullable: NULLABLE,
    initial: {
      code: detail?.code ?? '',
      legalName: detail?.legal_name ?? '',
      tradeName: detail?.trade_name ?? '',
      taxId: detail?.tax_id ?? '',
      customerType: detail?.customer_type ?? 'COMPANY',
      propertyManagerId: detail?.property_manager_id ?? '',
      presidentName: detail?.president_name ?? '',
      presidentPhone: detail?.president_phone ?? '',
      presidentEmail: detail?.president_email ?? '',
      maintenanceContactName: detail?.maintenance_contact_name ?? '',
      maintenanceContactPhone: detail?.maintenance_contact_phone ?? '',
      email: detail?.email ?? '',
      phone: detail?.phone ?? '',
      paymentTerms: detail?.payment_terms ?? '',
      salesRepUserId: detail?.sales_rep_user_id ?? '',
      accountingCode: detail?.accounting_code ?? '',
      accountingAccount: detail?.accounting_account ?? '',
      notes: detail?.notes ?? '',
    },
    onSubmit: async (body, client) => {
      if (editing) {
        return client.request<void>(`/customers/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ ...body, expectedVersion: detail?.version }),
        });
      }
      return client.post<{ id: string }>('/customers', body);
    },
    onSaved: (saved) => {
      void navigate(saved && 'id' in saved ? `/clientes/${saved.id}` : '/clientes');
    },
  });

  if (loadError !== null) {
    return (
      <section className="screen">
        <p className="screen__error" role="alert">
          {loadError}
        </p>
      </section>
    );
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">{editing ? 'Editar cliente' : 'Nuevo cliente'}</h1>
        {form.dirty && <span className="screen__count">Cambios sin guardar</span>}
      </header>

      {form.formError !== null && (
        <p className={`screen__error${form.versionConflict ? ' screen__error--conflict' : ''}`} role="alert">
          {form.formError}
          {form.versionConflict && (
            <button className="table__link" type="button" onClick={() => window.location.reload()}>
              Recargar
            </button>
          )}
        </p>
      )}

      {form.orphanErrors.length > 0 && (
        <p className="screen__error" role="alert">
          El servidor ha rechazado campos que este formulario no muestra:{' '}
          {form.orphanErrors.join(' · ')}
        </p>
      )}

      <form className="form" onSubmit={(e) => void form.submit(e)} noValidate>
        <FormSection title="Identificación">
          {id === undefined ? (
            <p className="hint">
              El código se asigna solo, siguiendo la serie de la casa: así no hay huecos ni
              dos clientes con el mismo.
            </p>
          ) : (
            <TextField
              label="Código"
              value={String(form.values['code'] ?? '')}
              onChange={(v) => form.set('code', v)}
              onBlur={() => form.blur('code')}
              error={form.errors['code']}
              maxLength={32}
            />
          )}
          <TextField
            label="Razón social"
            value={String(form.values['legalName'] ?? '')}
            onChange={(v) => form.set('legalName', v)}
            onBlur={() => form.blur('legalName')}
            error={form.errors['legalName']}
            required
            maxLength={255}
          />
          <TextField
            label="Nombre comercial"
            value={String(form.values['tradeName'] ?? '')}
            onChange={(v) => form.set('tradeName', v)}
            maxLength={255}
          />
          <TextField
            label="NIF"
            value={String(form.values['taxId'] ?? '')}
            onChange={(v) => form.set('taxId', v)}
            onBlur={() => {
              form.blur('taxId');
              void comprobarNif(String(form.values['taxId'] ?? ''));
            }}
            error={form.errors['taxId']}
            maxLength={20}
            help="Se puede dejar vacío ahora, pero hace falta para emitirle una factura."
          />
          {yaExiste !== null && (
            <div className="aviso-duplicado">
              <p>
                Ese NIF ya es de <strong>{yaExiste.legalName}</strong> ({yaExiste.code}). No
                hace falta crearlo otra vez.
              </p>
              <p className="hint">
                {yaExiste.sites.length === 0
                  ? 'Todavía no tiene ningún centro dado de alta.'
                  : `Centros: ${yaExiste.sites.map((c) => c.name).join(', ')}.`}
              </p>
              <Link className="button button--small" to={`/clientes/${yaExiste.id}`}>
                Ir a ese cliente
              </Link>
            </div>
          )}
          <SelectField
            label="Tipo"
            value={String(form.values['customerType'] ?? '')}
            onChange={(v) => form.set('customerType', v)}
            options={TYPES}
            required
            help="Las administraciones públicas facturan por FACe, no por correo."
          />
        </FormSection>

        {/* Sólo para comunidades: en una empresa estos campos no significan
            nada y llenarían la ficha de huecos que nadie rellena. */}
        {form.values['customerType'] === 'COMMUNITY' && (
          <FormSection title="Comunidad de propietarios">
            <SelectField
              label="Administrador de fincas"
              value={String(form.values['propertyManagerId'] ?? '')}
              onChange={(v) => form.set('propertyManagerId', v)}
              options={[
                { value: '', label: 'Sin administrador' },
                ...administradores.map((a) => ({
                  value: a.id,
                  label: `${a.name} · ${a.community_count} comunidades`,
                })),
              ]}
              help="Se elige de la lista, no se escribe: es lo que permite agrupar las comunidades por administrador. Si falta, créelo antes en Administradores de fincas."
            />
            <TextField
              label="Presidente"
              value={String(form.values['presidentName'] ?? '')}
              onChange={(v) => form.set('presidentName', v)}
              maxLength={255}
              help="Cambia cada año; conviene revisarlo al renovar el contrato."
            />
            <TextField
              label="Teléfono del presidente"
              value={String(form.values['presidentPhone'] ?? '')}
              onChange={(v) => form.set('presidentPhone', v)}
              maxLength={32}
            />
            <TextField
              label="Correo del presidente"
              type="email"
              value={String(form.values['presidentEmail'] ?? '')}
              onChange={(v) => form.set('presidentEmail', v)}
              maxLength={255}
            />
            <TextField
              label="Contacto de mantenimiento"
              value={String(form.values['maintenanceContactName'] ?? '')}
              onChange={(v) => form.set('maintenanceContactName', v)}
              maxLength={255}
              help="Quien abre la puerta el día de la revisión. Casi nunca es el presidente."
            />
            <TextField
              label="Teléfono de mantenimiento"
              value={String(form.values['maintenanceContactPhone'] ?? '')}
              onChange={(v) => form.set('maintenanceContactPhone', v)}
              maxLength={32}
            />
          </FormSection>
        )}

        <FormSection title="Contacto y pago">
          <TextField
            label="Correo electrónico"
            type="email"
            value={String(form.values['email'] ?? '')}
            onChange={(v) => form.set('email', v)}
            onBlur={() => form.blur('email')}
            error={form.errors['email']}
          />
          <TextField
            label="Teléfono"
            type="tel"
            value={String(form.values['phone'] ?? '')}
            onChange={(v) => form.set('phone', v)}
            onBlur={() => form.blur('phone')}
            error={form.errors['phone']}
            maxLength={32}
          />
          <TextField
            label="Condiciones de pago"
            value={String(form.values['paymentTerms'] ?? '')}
            onChange={(v) => form.set('paymentTerms', v)}
            maxLength={64}
            help="La Ley 3/2004 fija 30 días por defecto y 60 como máximo pactable entre empresas."
          />
          <SelectField
            label="Comercial asignado"
            value={String(form.values['salesRepUserId'] ?? '')}
            onChange={(v) => form.set('salesRepUserId', v)}
            options={[
              { value: '', label: 'Sin asignar' },
              ...salesReps.map((r) => ({
                value: r.id,
                label: `${r.full_name} · ${r.customer_count} clientes`,
              })),
            ]}
            help="Quien comisiona por este cliente. Dejarlo en «Sin asignar» lo libera para repartir."
          />
          <TextArea
            label="Notas"
            value={String(form.values['notes'] ?? '')}
            onChange={(v) => form.set('notes', v)}
          />
        </FormSection>

        <FormSection
          title="Contabilidad"
          description="Sólo para la exportación a A3. Si se dejan vacíos, el código se deriva del interno y A3 asigna la subcuenta."
        >
          <TextField
            label="Código en A3"
            value={String(form.values['accountingCode'] ?? '')}
            onChange={(v) => form.set('accountingCode', v)}
            maxLength={20}
            help="El código con el que A3 conoce a este cliente, si ya lo tiene dado de alta."
          />
          <TextField
            label="Subcuenta contable"
            value={String(form.values['accountingAccount'] ?? '')}
            onChange={(v) => form.set('accountingAccount', v)}
            maxLength={20}
            help="La del mayor, normalmente 430…"
          />
        </FormSection>

        <footer className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear cliente'}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              if (form.dirty && !window.confirm('Hay cambios sin guardar. ¿Salir igualmente?')) return;
              void navigate(editing ? `/clientes/${id}` : '/clientes');
            }}
          >
            Volver
          </button>
        </footer>
      </form>
    </section>
  );
}
